/**
 * NoteComposer — the note-pipeline entry point (Kith hackathon, v4
 * concept). A CM posts one narrative note; Kith reads it and everything
 * downstream (billing line, confirm queue) hangs off that single post.
 *
 * Anatomy (dense-but-warm, tokens only):
 *   - textarea with a per-client localStorage draft (restore on mount,
 *     autosave on change, cleared optimistically on post);
 *   - rate-band segmented pills rendered from a config array prop
 *     (RATE_BANDS default; labels live in one place, not scattered),
 *     when the bands offered are exactly the two SPICE bands (a
 *     SPICE-only seat on a SPICE client), the pills give way to the
 *     SPICE | Friendly Visitor switch (SpiceBandToggle): the one place the
 *     session type is picked (the SPICE doors hand the note here and pick
 *     nothing). A fresh SPICE compose starts on the client's own session
 *     kind (useSpiceBandDefault) until the person picks; its hours start
 *     at 1 on SPICE, editable
 *     (spiceDefaultHoursForBand); a SPICE band posts a session note only
 *     (no assessment door);
 *   - hours numeric input; occurred-on date (defaults today); the
 *     Regular | After Hours toggle beside them (the CM's assertion, never
 *     derived), with a line under it that states when the date is after
 *     hours by definition (a weekend or a recognized holiday) and, on
 *     Travel, where after-hours travel belongs; the line never sets the
 *     toggle;
 *   - 20,000-char hard cap (abuse backstop only; founder ruling: pasted
 *     email threads are normal input) with a QUIET count past 1,500;
 *   - Post (disabled while empty) → optimistic clear + the front-and-
 *     center "Reading your note…" beat with a live elapsed counter
 *     (ReadingIndicator — WS-9 P0). The beat is REAL: after the
 *     note POST succeeds the composer AWAITS the extraction route
 *     (POST .../notes/{id}/extract — the Era-A production invoker; the
 *     client-side await keeps the server invocation alive on SWA), then
 *     reads the proposals queue once. Extraction failure hands off to
 *     the feed's extraction_failed retry machinery.
 *   - the server's same-day refusal (a SPICE visit note is already on
 *     file for this client today) is a question, not an error: post a
 *     second visit anyway (the same note re-posts carrying
 *     second_visit_confirmed, then reads like a first post) or keep the
 *     first note, with the typed note back in the box either way. Every
 *     other refusal shows the server's own message on the status line.
 *
 * Composes Button + Field + Chip only (raw-button lint holds); page
 * hosts it inside PageShell.
 */

import { type KeyboardEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { SegmentedToggle } from '@/components/primitives/SegmentedToggle';
import { nextRadioIndex, radioStep } from '@/lib/radioGroupKeys';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/lib/auth';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { canViewAllClinical } from '@/lib/capabilities';
import {
  hoursIncrementMessage,
  isOnHoursIncrement,
  readHoursIncrement,
} from '@/lib/hoursIncrement';
import {
  ADMIN_RATE_BAND,
  NOT_THE_AUTHOR_EDIT_LINE,
  PAY_PERIOD_CLOSED_EDIT_LINE,
  PAY_PERIOD_CLOSED_POST_LINE,
  TRAVEL_RATE_BAND,
  editorIsNotAuthor,
  isNativeNote,
  notesApi,
  bandFamily,
  isPerVisitBand,
  readRateBands,
  noteDateRefusal,
  runExtractionToCompletion,
  serverErrorCodeFrom,
  serverMessageFrom,
  type NotesListResponse,
  type NoteWire,
  type RateBandOption,
} from '@/lib/notes';
import { gaspPhrase } from '@/lib/gaspSummary';
import { errorText, noteReadKeyOf, refusalText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { bandOptionsForEngagement, bandOptionsForWhoami, isSpiceBand } from '@/lib/bandVisibility';
import { afterHoursDay, recognizedHolidays } from '@/lib/afterHoursDays';
import { noteDateDefault, practiceTodayIso } from '@/lib/practiceDate';
import type { ClientEngagement } from '@/lib/clientRecord';
import { clearNotePrefill, peekNotePrefill, type NotePrefill } from '@/lib/notePrefill';
import { NOTE_DRAFT_PREFIX, readStoredDraft, writeStoredDraft } from '@/lib/draftAutosave';
import {
  noteComposerFieldsKey,
  readNoteComposerFields,
  writeNoteComposerFields,
  type NoteComposerFields,
} from '@/lib/noteComposerFields';
import { KeptWorkNotice } from '@/components/KeptWorkNotice';
import { keptWorkKey, useKeptWork } from '@/lib/keptWork';
import { noteDraftsApi, noteDraftsQueryKey, type NoteDraftWire } from '@/lib/noteDrafts';
import { invalidateMyHours } from '@/lib/timeEntriesApi';
import { invalidateInvoiceReads } from '@/lib/invoiceInvalidation';
import { Button } from '@/components/primitives/Button';
import { Field } from '@/components/primitives/Field';
import { CheckboxRow } from '@/components/primitives/CheckboxRow';
import { longDistanceHelper, readLongDistanceRule } from '@/lib/longDistanceTravelPay';
import { AfterHoursToggle } from '@/components/time/AfterHoursToggle';
import { TextArea } from '@/components/primitives/TextInput';
import { ReadingIndicator, type ReadingKind } from '@/components/notes/ReadingIndicator';
import { SecondVisitQuestion } from '@/components/notes/SecondVisitQuestion';
import { FLD, FLD_LABEL, FLD_TEXTAREA } from '@/components/primitives/modalFormStyles';
import { SPICE_BAND, SPICE_FRIENDLY_VISITOR_BAND, spiceDefaultHoursForBand } from '@/lib/spiceVisitNote';
import { useSpiceBandDefault } from '@/lib/spiceBandDefault';
import { SpiceBandToggle } from '@/components/notes/SpiceBandToggle';
import { DateInput } from '@/components/primitives/DateInput';

/**
 * p75 of real notes is 1,685 chars and a meaty pasted-email note is
 * NORMAL input — the limit exists only as an abuse backstop and must
 * never block a real note. The scrubber handles paste bloat server-side.
 * The server refuses the same number (NOTE_RAW_TEXT_MAX_CHARS in
 * api/_shared/validators/encounterNotes.ts); keep the two equal.
 */
export const NOTE_CHAR_LIMIT = 20000;

/**
 * The Rate picker's shape (LP-28). Below `sm` a two-column grid at full
 * width: every pill an equal cell, an odd last pill across both columns,
 * so the choices are full rows and never a ragged tail. From `sm` up the
 * content-sized row that wraps whole pills (S3.5). Written out whole so
 * Tailwind finds every class.
 */
export const RATE_BANDS_GROUP_CLS =
  'grid w-full grid-cols-2 gap-1 rounded-base bg-sand p-1 sm:inline-flex sm:w-auto sm:flex-wrap';

/** A pill in that grid: fills its cell, wraps between words, and breaks
 *  inside a word only when the word alone cannot fit (a phone at 200%
 *  text), so nothing ever runs past its fill; an odd last pill spans the
 *  row; content-sized from `sm` up. */
export const RATE_BAND_PILL_CLS =
  'min-w-0 w-full text-center leading-tight [overflow-wrap:anywhere] last:odd:col-span-2 sm:w-auto';

/** Show the quiet character count only past this (no scolding copy). */
const COUNTER_VISIBLE_FROM = 1500;

/** An after-hours call-out is one care management note with its travel
 *  inside it; on the Travel band the composer says so. */
const AFTER_HOURS_TRAVEL_SENTENCE =
  'After Hours travel goes in the care management note, with the travel included in its hours.';

/**
 * The line under the Regular | After Hours toggle, or null for none. It
 * states a fact and never sets the toggle.
 *
 * The day part: while the toggle is offered and reads Regular, the hours
 * are above zero, and the date is a weekend or a recognized holiday, it
 * says so ("Saturday is After Hours.", "{holiday} is After Hours."). Every
 * band the toggle is offered on follows this one rule, SPICE and Friendly
 * Visitor included (LP-42).
 *
 * The Travel part: on the Travel band, whenever the day part shows or
 * After Hours is chosen, the one-entry sentence follows.
 */
function afterHoursLineFor({
  band,
  offered,
  afterHours,
  hours,
  day,
}: {
  band: string;
  offered: boolean;
  afterHours: boolean;
  hours: string;
  day: ReturnType<typeof afterHoursDay>;
}): string | null {
  if (!offered) return null;
  let dayPart: string | null = null;
  if (!afterHours && Number(hours) > 0 && day !== null) {
    dayPart = `${day.kind === 'holiday' ? day.name : day.weekday} is After Hours.`;
  }
  const travelPart = band === TRAVEL_RATE_BAND && (dayPart !== null || afterHours) ? AFTER_HOURS_TRAVEL_SENTENCE : null;
  const parts = [dayPart, travelPart].filter((p): p is string => p !== null);
  return parts.length > 0 ? parts.join(' ') : null;
}

type ReadingState =
  | { phase: 'idle' }
  /** kind is the door the note POSTED through, captured when Post fires:
   * the toggle resets once the post lands, while the read is still
   * running. progress arrives when the server reads the note a section
   * at a time (an assessment always; a case note long enough to need
   * it) and the loop reports after each. */
  | { phase: 'reading'; kind: ReadingKind; progress?: { done: number; total: number } }
  /** B1 the Gasp (Design Pass 2): the reveal carries what the note
   * BECAME — the fresh proposals' types (for the category sentence) and
   * whether the invoice line was drafted (null = unknown; the sentence
   * is simply omitted — never guessed). */
  | { phase: 'done'; found: number; categories: string[]; invoiceLineDrafted: boolean | null }
  /** Extraction finished; nothing new needs confirming (a real outcome —
   * e.g. a plain travel note). */
  | { phase: 'none' }
  /** The note POSTED but the read did not run or did not finish; key is
   * the note_read copy line (noteReadKeyOf): a refusal at the read
   * allowance or the day's budget says so, anything else is the plain
   * read failure. The feed's retry / read-now doors own the recovery. */
  | { phase: 'read_failed'; key: string }
  /** The post was refused because a visit note is already on file for
   * this client today. The note is back in the box and the composer asks
   * whether to post a second visit; either answer leaves this phase. */
  | { phase: 'second_visit_asked' }
  /** message = server-written on-screen copy when it sent one (e.g. the
   * on-invoice edit refusal); absent = the generic line. */
  | { phase: 'error'; message?: string };

/** What one press of Post carries: the note text (the box clears
 * optimistically, so the text travels with the call) and whether the
 * author has said a second visit today is real. */
/** Everything a post sends and its callbacks read, captured when Post is
 *  pressed: React Query hands a mutation its options in an
 *  effect, so a mutationFn or callback reading this render's band, hours
 *  or date could act on an earlier render's. */
interface PostVariables {
  rawText: string;
  /** The door the note posts through, captured at Post time like the
   * text: the toggle moves on before the read settles. */
  kind: 'note' | 'assessment';
  input: Parameters<typeof notesApi.createNote>[2];
  /** The note an edit replaces, or null for a new note. */
  supersedeId: string | null;
  /** The band posted: the box resets to its default hours after. */
  band: string;
  /** The server draft the post consumes, or null. */
  savedDraftId: string | null;
  /** An edit, or a box opened from a server draft: the host gets the box back. */
  isSupersede: boolean;
  /** An edit of a note that is not native (the edited note's source as
   *  the host passed it): the beat at Post is not set for it. */
  editedNotNative: boolean;
  fromServerDraft: boolean;
}

/** What an expired sign-in keeps of the composer (src/lib/keptWork.ts). */
interface KeptNote {
  text: string;
  hours: string;
  band: string;
  afterHours: boolean;
  /** Absent on a copy kept before LP-46: reads as unticked. */
  longDistance?: boolean;
  occurredAt: string;
  kind: 'note' | 'assessment';
}

/** What a draft save sends and its callbacks read, captured when pressed. */
interface DraftSaveVariables {
  input: Parameters<typeof noteDraftsApi.create>[2];
  savedDraftId: string | null;
  localAutosaveOff: boolean;
}

export interface NoteComposerProps {
  clientId: string;
  /** Rate-band config (value + label pairs); when omitted, resolved from
   * tenants.config.features.rate_bands via useTenantConfig with the
   * RATE_BANDS fallback (D2 config seam). */
  rateBands?: readonly RateBandOption[];
  /**
   * clients.engagement, resolved to the face the host is showing (a
   * 'both' client's host passes whichever side its toggle is on): a
   * 'spice' face speaks session vocabulary throughout the composer
   * (heading, helper line, Post button, terracotta heading), offers only
   * the two SPICE rate bands, and has no Case note / Assessment toggle,
   * because a session note is the one kind; 'both' keeps the full
   * case-note vocabulary and band row; 'care_management' (or omitted,
   * for a host that has no engagement to pass) is the plain case-note
   * composer.
   */
  engagement?: ClientEngagement;
  charLimit?: number;
  /** Fires when the post-extract read finds new proposals (queue refresh hook). */
  onProposalsArrived?: () => void;
  /**
   * Which door the composer opens on (CM-home deep link: the new-client
   * "Add it" nudge lands on the assessment toggle via ?kind=assessment).
   * Mount-time only; the kind still resets to 'note' after every post.
   * Ignored on a SPICE face, which has no assessment door.
   */
  initialKind?: 'note' | 'assessment';
  /**
   * 0125 edit-as-new-version: when set, the composer opens PREFILLED
   * from this note and posting SUPERSEDES it (the server creates the
   * replacement and retires the old note with a version link, one
   * transaction; the replacement keeps the old note's source).
   * Extraction then runs on the new note exactly like a fresh post when
   * the replacement is native; a replacement that is not native is never
   * read, and the box goes back to the host at once. Mount-time only:
   * hosts remount with a key when the target changes. No draft autosave in this mode (the CM's unposted
   * new-note draft is never clobbered).
   */
  supersede?: { note: NoteWire } | null;
  /** Fires after a successful save-as-new-version. */
  onSupersedeDone?: () => void;
  /** Fires when the CM backs out of the edit. */
  onSupersedeCancel?: () => void;
  /**
   * 0130 post-a-draft: when set, the composer opens PREFILLED from this
   * server draft (body/kind/hours/occurred_on + rate band + after-hours)
   * and posting CONSUMES it — the server deletes the draft in the same
   * transaction as the note insert. "Save as draft" updates the same
   * draft in place. Mount-time only — hosts remount with a key when the
   * target changes. localStorage autosave is off in this mode (the
   * server draft owns the box; the unposted new-note autosave is never
   * clobbered).
   */
  serverDraft?: NoteDraftWire | null;
  /** Fires after the draft is posted (consumed) or its edit is backed out. */
  onServerDraftDone?: () => void;
}

function draftKey(tenantSlug: string, clientId: string): string {
  return `${NOTE_DRAFT_PREFIX}:${tenantSlug}:${clientId}`;
}

/**
 * Draft TTL + storage live in the shared src/lib/draftAutosave.ts (one
 * implementation; the Assessment Drafter's notes field reuses it).
 * Re-exported here so existing imports keep working.
 */
export { DRAFT_TTL_MS } from '@/lib/draftAutosave';

/** The composer's kind, as the house SegmentedToggle's two radios. */
const KIND_OPTIONS = [
  { value: 'note', label: 'Case note', suffix: 'note' },
  { value: 'assessment', label: 'Assessment', suffix: 'assessment' },
] as const;

export function NoteComposer({
  clientId,
  rateBands: rateBandsProp,
  engagement,
  charLimit = NOTE_CHAR_LIMIT,
  onProposalsArrived,
  initialKind = 'note',
  supersede = null,
  onSupersedeDone,
  onSupersedeCancel,
  serverDraft = null,
  onServerDraftDone,
}: NoteComposerProps): ReactNode {
  const { tenantSlug, whoami, principal } = useTenant();
  // The local draft is stamped with, and restored only to, the person
  // signed in: the platform's principal id.
  const draftAuthor = principal?.userId ?? null;
  // D2 config seam: rate bands from tenants.config.features.rate_bands,
  // RATE_BANDS fallback (readRateBands). The `band` initializer below
  // runs once at mount, so a config override that loads after mount
  // updates the OPTIONS but not the already-defaulted selection; the
  // 10-min-stale shared cache (AppShell warms it) makes that window
  // cold-load-only. Peace (no config row) is byte-identical.
  //
  // 0134 rate-band matrix: the offered pills are scoped to the actor
  // (whoami.role + whoami.does_spice) through the tenant's band-visibility
  // matrix — a care manager sees Care management + Travel, admin support
  // sees Admin, a does_spice staffer gains the SPICE bands, owner/steward/
  // exec see all. The server enforces the same set on the write path; this
  // filter is UX. An explicit rateBands prop bypasses the filter (tests /
  // embedded hosts own their options).
  const { data: tenantConfig } = useTenantConfig(tenantSlug);
  const isSpiceEngagement = engagement === 'spice';
  const rateBands = bandOptionsForEngagement(
    engagement,
    rateBandsProp ??
      bandOptionsForWhoami(whoami, tenantConfig?.config, readRateBands(tenantConfig?.config), {
        // Editing a note keeps its own band even when that band has been
        // switched off since; the server lets the same band through.
        keep: supersede?.note.rate_band || null,
      }),
  );
  // WS-9: error copy comes from the registry; the escalation contact
  // from the tenants.config.support.contact seam.
  const supportContact = useSupportContact();
  const queryClient = useQueryClient();
  const key = draftKey(tenantSlug, clientId);
  const supersedeNote = supersede?.note ?? null;
  // LP-45: the edited note's pay period has closed and this seat is not
  // the office, the server's word (pay_inputs_locked). The band, hours,
  // date, time of day and long-distance tick stay as stored (shown, not
  // choosable) and are posted unchanged; the words still change.
  const payLocked = supersedeNote?.pay_inputs_locked === true;
  const isSupersede = supersedeNote !== null;
  // 0130: the composer is working ON a server draft (opened from "Your
  // drafts" — mount-time prefill; posting consumes it) or has SAVED one
  // from this box ("Save as draft" — later saves update it in place and
  // the eventual post consumes it too). Never in supersede mode.
  const [savedDraftId, setSavedDraftId] = useState<string | null>(serverDraft?.id ?? null);
  const [draftPhase, setDraftPhase] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Email-door prefill (src/lib/notePrefill.ts): a drafted email arrives
  // here as narrative + suggested hours + occurred date. Peek on first
  // render, clear in the mount effect below; peek-then-clear (rather than
  // read-and-clear inside an initializer) keeps consumption idempotent
  // under React's dev double-render. The CM reviews before posting;
  // nothing auto-posts. Skipped entirely in supersede mode — the note
  // being edited owns the box.
  const [prefill] = useState<NotePrefill | null>(() =>
    isSupersede || serverDraft !== null ? null : peekNotePrefill(tenantSlug, clientId),
  );
  const [showPrefillHint, setShowPrefillHint] = useState<boolean>(prefill !== null);
  // What the person picked or typed beside the note in this tab, kept
  // with the note's own words (noteComposerFields.ts): a door that leaves
  // the record and the Back that returns find the note as it was. Only a
  // fresh compose with words in its draft restores them. A prefill's own
  // hours and date outrank them, and a SPICE door's prefill keeps only a
  // SPICE band.
  const fieldsKey = noteComposerFieldsKey(tenantSlug, clientId);
  const [keptFields] = useState<NoteComposerFields>(() =>
    isSupersede || serverDraft !== null || readStoredDraft(key, draftAuthor).trim() === ''
      ? {}
      : readNoteComposerFields(fieldsKey, draftAuthor),
  );
  const keptBand =
    keptFields.band !== undefined && (prefill?.service !== 'spice' || isSpiceBand(keptFields.band))
      ? keptFields.band
      : undefined;

  const [text, setText] = useState<string>(() => {
    if (supersedeNote !== null) return supersedeNote.raw_text;
    if (serverDraft !== null) return serverDraft.body;
    const draft = readStoredDraft(key, draftAuthor);
    if (prefill === null) return draft;
    // Never clobber words the CM already has in the box: append below. A
    // SPICE door's narrative leads instead, so the note's first line stays
    // the builder's stamp (the SPICE tab reads the session's kind from it).
    const merged =
      draft === ''
        ? prefill.narrative
        : prefill.service === 'spice'
          ? `${prefill.narrative}\n\n${draft}`
          : `${draft}\n\n${prefill.narrative}`;
    return merged.length > charLimit ? merged.slice(0, charLimit) : merged;
  });
  const [band, setBand] = useState<string>(
    () =>
      // A locked note shows its stored band, none included (LP-45).
      (payLocked ? supersedeNote!.rate_band : null) ??
      (supersedeNote !== null && supersedeNote.rate_band !== '' ? supersedeNote.rate_band : null) ??
      serverDraft?.rate_band ??
      keptBand ??
      rateBands[0]?.value ??
      'care_management',
  );
  // The kind affordance: default is the case note; 'assessment' is the
  // quiet paste-an-assessment door that seeds the record. Resets to the
  // default after every post. In supersede mode it starts on the edited
  // note's door (an edited assessment stays an assessment), and a saved
  // draft keeps the kind it was saved with. A SPICE face has no
  // assessment door (a session note is the one kind), so a fresh compose
  // there starts on 'note' whatever door a deep link asked for.
  const [kind, setKind] = useState<'note' | 'assessment'>(() => {
    if (supersedeNote !== null) return supersedeNote.kind === 'assessment' ? 'assessment' : 'note';
    if (serverDraft !== null) return serverDraft.kind;
    return isSpiceEngagement ? 'note' : initialKind;
  });
  const [hours, setHours] = useState<string>(() => {
    // Supersede prefills the note's own hours — '0' stays '0' (a valid
    // no-charge value under the #431 rule; only BLANK blocks Post).
    if (supersedeNote !== null) return String(supersedeNote.hours);
    // A draft's hours may be BLANK by design — the wall applies at post.
    if (serverDraft !== null) return serverDraft.hours != null ? String(serverDraft.hours) : '';
    if (prefill?.hours != null) return String(prefill.hours);
    if (keptFields.hours !== undefined) return keptFields.hours;
    // A fresh compose starts on the band's own default (1 on SPICE).
    return spiceDefaultHoursForBand(band);
  });
  // A fresh compose's hours box follows the band (spiceDefaultHoursForBand)
  // until the person types there. An edit or a saved draft never follows:
  // its stored hours stand, blank included; nor do hours a prefill brought.
  const hoursFollowBandRef = useRef(
    supersede?.note == null && serverDraft === null && prefill?.hours == null && keptFields.hours === undefined,
  );
  // Every band change goes through here, so a following hours box moves
  // in the same render as the band (never a frame behind it).
  const applyBand = (next: string): void => {
    setBand(next);
    if (hoursFollowBandRef.current) setHours(spiceDefaultHoursForBand(next));
  };
  // A 0h case note gets a one-time friendly confirm before it posts
  // (founder 2026-07-06: "a friendly reminder, not a ban").
  const [confirmingZero, setConfirmingZero] = useState(false);
  const [afterHours, setAfterHours] = useState(
    () => supersedeNote?.after_hours ?? serverDraft?.after_hours ?? keptFields.afterHours ?? false,
  );
  // The long-distance tick (LP-46): an Edit opens with the note's own, a
  // box opened from a saved draft with the draft's.
  const [longDistance, setLongDistance] = useState(() => {
    if (supersedeNote !== null) return supersedeNote.long_distance_travel === true;
    if (serverDraft !== null) return serverDraft.long_distance_travel === true;
    return keptFields.longDistance ?? false;
  });
  const [occurredAt, setOccurredAt] = useState<string>(() => {
    // The note's day arrives as its 'YYYY-MM-DD' text (a DATE column).
    if (supersedeNote !== null) return supersedeNote.occurred_at;
    if (serverDraft !== null) return serverDraft.occurred_on;
    return prefill?.occurred_at ?? keptFields.occurredAt ?? noteDateDefault();
  });
  // The server's refusal of the date, shown under the date box until the
  // box next changes or a new post goes out.
  const [dateRefusal, setDateRefusal] = useState<string | null>(null);
  const occurredRef = useRef<HTMLInputElement>(null);
  const [reading, setReading] = useState<ReadingState>({ phase: 'idle' });
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // 0134 — keep the SELECTION inside the actor's allowed set once whoami/
  // config resolve after mount (e.g. admin support mounts before whoami and
  // the initializer defaults to the first vocabulary band; the filter then
  // narrows the options to Admin). Also covers an edit/draft prefill whose
  // band the actor may no longer pick — the selection snaps to their first
  // allowed band, visibly, instead of posting into the server's reject.
  useEffect(() => {
    // A locked note keeps its stored band, whoever edits it: moving it
    // would be the change the pay-period lock refuses.
    if (payLocked) return;
    if (rateBands.length > 0 && !rateBands.some((rb) => rb.value === band)) {
      applyBand(rateBands[0]!.value);
    }
  }, [rateBands, band, payLocked]);

  // A fresh SPICE compose starts on the client's own session kind
  // (Friendly Visitor when every one of the client's sessions is one)
  // until the person picks a band on the pills: when the first band
  // offered is a SPICE band and Friendly Visitor is offered, or when a
  // SPICE door's prefill opened the box and SPICE is offered. An edited
  // note or a saved draft keeps the band it carries.
  const offers = (value: string): boolean => rateBands.some((rb) => rb.value === value);
  // A seat offered exactly the two SPICE bands picks between them with the
  // SPICE | Friendly Visitor switch.
  // One SPICE-family per-visit band beside the pair is drawn as a
  // third segment, so the switch stays; SPICE stays the default.
  const tenantBands = readRateBands(tenantConfig?.config);
  const perVisitSpiceOffered = rateBands.filter(
    (rb) => isPerVisitBand(rb.value, tenantBands) && bandFamily(rb.value, tenantBands) === 'spice',
  );
  const sessionOffered = rateBands.filter((rb) => isSpiceBand(rb.value));
  const offersOnlySpiceBands =
    sessionOffered.length === 2 &&
    sessionOffered[0]!.value !== sessionOffered[1]!.value &&
    perVisitSpiceOffered.length <= 1 &&
    rateBands.length === 2 + perVisitSpiceOffered.length;
  const spiceToggleExtra = perVisitSpiceOffered[0] ?? null;
  const followsSpiceDefault =
    !isSupersede &&
    serverDraft === null &&
    ((rateBands.length > 0 && isSpiceBand(rateBands[0]!.value) && offers(SPICE_FRIENDLY_VISITOR_BAND)) ||
      (prefill?.service === 'spice' && offers(SPICE_BAND)));
  const spiceDefaultBand = useSpiceBandDefault(tenantSlug, clientId, { enabled: followsSpiceDefault });
  // A band kept from this tab was picked by the person: it stands.
  const bandPickedRef = useRef(keptBand !== undefined);
  useEffect(() => {
    if (followsSpiceDefault && !bandPickedRef.current) applyBand(spiceDefaultBand);
  }, [followsSpiceDefault, spiceDefaultBand]);
  const pickBand = (next: string): void => {
    bandPickedRef.current = true;
    applyBand(next);
  };

  // The band pills are one radio group: ArrowRight and ArrowDown check the
  // next pill, ArrowLeft and ArrowUp the previous, wrapping, and the focus
  // follows the check (radioGroupKeys).
  const bandRadios = useRef<Array<HTMLButtonElement | null>>([]);
  const onBandKey = (e: KeyboardEvent<HTMLButtonElement>): void => {
    const step = radioStep(e.key);
    if (step === 0) return;
    e.preventDefault();
    const next = nextRadioIndex(rateBands.findIndex((x) => x.value === band), step, rateBands.length);
    if (next < 0) return;
    pickBand(rateBands[next]!.value);
    bandRadios.current[next]?.focus();
  };

  // A SPICE band posts a session note only: the assessment door belongs
  // to care management, and the server reads a SPICE-band note with the
  // SPICE reader whatever its kind.
  const onSpiceBand = bandFamily(band, tenantBands) === 'spice';
  useEffect(() => {
    if (onSpiceBand) setKind('note');
  }, [onSpiceBand]);
  // A long-distance visit pays the author and only a SPICE-family band
  // carries it: the tick is offered there alone, and a move to any other
  // band clears it rather than posting a tick the server refuses.
  useEffect(() => {
    if (!onSpiceBand) setLongDistance(false);
  }, [onSpiceBand]);

  // There is no after-hours admin (rateBands.ts afterHoursBandFor
  // returns null for it, and the server refuses the pair): the toggle is
  // never offered on an admin note (below), and switching TO admin while
  // After hours is chosen clears it rather than posting a flag the band
  // can't act on.
  // A per-visit band is never after hours either: the toggle and
  // the nudge are not offered, and switching to one clears the flag.
  const isAdminBand = band === ADMIN_RATE_BAND;
  const noAfterHoursBand = isAdminBand || isPerVisitBand(band, tenantBands);
  useEffect(() => {
    if (noAfterHoursBand) setAfterHours(false);
  }, [noAfterHoursBand]);

  // Consume the prefill: once it is in component state, the storage copy
  // has served its purpose (clearNotePrefill is idempotent).
  useEffect(() => {
    if (prefill !== null) clearNotePrefill(tenantSlug, clientId);
  }, [prefill, tenantSlug, clientId]);

  // Autosave the narrative to the per-client localStorage draft on every
  // change (the in-flight safety net) — never in supersede mode, and
  // never while working ON a server draft (either way the box belongs to
  // an existing thing; the unposted new-note autosave is not clobbered).
  const localAutosaveOff = isSupersede || serverDraft !== null;
  useEffect(() => {
    if (!localAutosaveOff) writeStoredDraft(key, text, draftAuthor);
  }, [key, text, localAutosaveOff, draftAuthor]);

  /**
   * The Era-A extraction drive (production-bug fix): the composer AWAITS
   * the extract route after a successful post; a client-side await is
   * what keeps the server invocation alive on SWA (fire-and-forget after
   * a response is killed; no queue/timer triggers). Extraction is
   * synchronous server-side, so when the call
   * resolves the proposals are already committed, one queue read
   * settles the beat; no polling.
   *
   * NOTE: the reading beat lives past the composer's own unmount —
   * the fetch keeps running (extraction completes or fails server-side)
   * and so does the cache invalidation below: it is a queryClient call,
   * not a state update, so it never checks mountedRef. Only the LOCAL
   * beat (setReading, the proposals re-read) stops once this instance is
   * gone; the feed picks up the result on its own next look regardless —
   * including when this composer was replaced mid-read (Edit on another
   * note, opening a draft — both remount it under a new key) or the
   * operator simply left the page before the retries finished.
   */
  async function extractThenReport(
    noteId: string,
    baselineIds: ReadonlySet<string>,
    postedKind: ReadingKind,
  ): Promise<void> {
    try {
      // An assessment, or a case note long enough to need chunking, is
      // read a section at a time (the server answers 'partial' per
      // chunk); the loop keeps calling and reports progress so the beat
      // reads "Reading your note… section 2 of 5 · 48s", or "Reading the
      // assessment…" when the posted note is one. A short case note
      // completes in the single call, progress never fires.
      await runExtractionToCompletion(tenantSlug, noteId, (done, total) => {
        if (mountedRef.current) {
          setReading({ phase: 'reading', kind: postedKind, progress: { done, total } });
        }
      });
    } catch (err) {
      // The note POSTED (nothing is lost); either the server's status
      // machine flipped extraction_failed or the read was refused before
      // it ran (the note stays 'posted') — refresh the feed so its retry
      // / read-now affordance (same route) takes over. Unconditional:
      // this is the only signal that tells the feed to refetch, so it
      // must not wait on this composer instance still being mounted.
      void queryClient.invalidateQueries({ queryKey: ['kith-notes', tenantSlug, clientId] });
      // The sections that did land wrote their proposals; the confirm
      // queue shows them now rather than after some later refetch.
      void queryClient.invalidateQueries({ queryKey: ['kith-proposals', tenantSlug, clientId] });
      if (!mountedRef.current) return;
      setReading({ phase: 'read_failed', key: noteReadKeyOf(err) });
      return;
    }
    // Status + draft invoice line are now final on the note row.
    // Unconditional for the same reason as the failure branch above — the
    // feed's own refetch must not depend on this instance surviving.
    // AWAITED (B1): the refetch settles first so the Gasp below can read
    // the note's fresh draft-line state out of the feed cache — no extra
    // request.
    await queryClient
      .invalidateQueries({ queryKey: ['kith-notes', tenantSlug, clientId] })
      .catch(() => {
        /* the feed's own retry owns any refetch failure */
      });
    if (!mountedRef.current) return;
    // The posted note's draft-line fact, from the already-refetched feed
    // cache (any page). Not found (feed unmounted / other page) → null →
    // the Gasp omits the invoice sentence rather than guessing.
    let invoiceLineDrafted: boolean | null = null;
    for (const [, cached] of queryClient.getQueriesData<NotesListResponse>({
      queryKey: ['kith-notes', tenantSlug, clientId],
    })) {
      const hit = cached?.notes.find((n) => n.id === noteId);
      if (hit !== undefined) {
        invoiceLineDrafted = hit.invoice_summary_draft !== null;
        break;
      }
    }
    try {
      const res = await notesApi.listProposals(tenantSlug, clientId);
      if (!mountedRef.current) return;
      const fresh = res.proposals.filter((p) => !baselineIds.has(p.id));
      queryClient.setQueryData(['kith-proposals', tenantSlug, clientId], res);
      if (fresh.length > 0) {
        setReading({
          phase: 'done',
          found: fresh.length,
          categories: fresh.map((p) => p.proposal_type),
          invoiceLineDrafted,
        });
        onProposalsArrived?.();
      } else {
        setReading({ phase: 'none' });
      }
    } catch {
      if (!mountedRef.current) return;
      // Extraction itself succeeded; only the queue refresh missed —
      // invalidate so the queue's own refetch shows whatever landed.
      void queryClient.invalidateQueries({ queryKey: ['kith-proposals', tenantSlug, clientId] });
      setReading({ phase: 'none' });
    }
  }

  /** The post as it stands when Post is pressed. */
  function postVariables(rawText: string, postedKind: 'note' | 'assessment', secondVisitConfirmed: boolean): PostVariables {
    // LP-45: a locked note posts its payroll facts exactly as stored,
    // whatever the boxes came to show (a band snapped, a flag cleared);
    // only the words are new. A blank band stays blank.
    const facts =
      payLocked && supersedeNote !== null
        ? {
            occurred_at: supersedeNote.occurred_at,
            rate_band: supersedeNote.rate_band,
            hours: supersedeNote.hours,
            after_hours: supersedeNote.after_hours,
            long_distance_travel: supersedeNote.long_distance_travel === true,
          }
        : {
            occurred_at: occurredAt,
            rate_band: band,
            hours: hours.trim() === '' ? null : Number(hours),
            after_hours: afterHours,
            // Cleared on any band outside the SPICE family (the effect above).
            long_distance_travel: longDistance,
          };
    return {
      rawText,
      kind: postedKind,
      input: {
        ...facts,
        raw_text: rawText,
        kind: isSpiceBand(facts.rate_band) ? 'note' : postedKind,
        // 0130 post-consumes-draft: the server deletes the draft in the
        // same transaction as the note insert.
        ...(savedDraftId !== null && supersedeNote === null ? { draft_id: savedDraftId } : {}),
        // The author's word that a second visit today is real: sent only
        // once the same-day question has been answered yes, never on an
        // ordinary post.
        ...(secondVisitConfirmed ? { second_visit_confirmed: true } : {}),
      },
      supersedeId: supersedeNote?.id ?? null,
      band,
      savedDraftId,
      isSupersede,
      editedNotNative: supersedeNote !== null && !isNativeNote(supersedeNote),
      fromServerDraft: serverDraft !== null,
    };
  }

  // The note in flight: the box clears the moment Post is pressed, so an
  // expired sign-in meeting the post keeps the note as it was posted.
  const inFlightRef = useRef<KeptNote | null>(null);
  // Successful posts: the kept copy, if any, has done its job.
  const [postedCount, setPostedCount] = useState(0);
  const kept = useKeptWork<KeptNote>(
    isSupersede
      ? keptWorkKey(tenantSlug, 'note-edit', supersedeNote.id)
      : serverDraft !== null
        ? keptWorkKey(tenantSlug, 'note-draft', serverDraft.id)
        : keptWorkKey(tenantSlug, 'note', clientId),
    () =>
      inFlightRef.current ??
      (text.trim() === '' ? null : { text, hours, band, afterHours, longDistance, occurredAt, kind }),
    (k) => {
      hoursFollowBandRef.current = false;
      setText(k.text);
      setKind(k.kind);
      // A locked note's four facts stay as stored: only the words come
      // back (LP-45).
      if (payLocked) return;
      setHours(k.hours);
      setBand(k.band);
      setAfterHours(k.afterHours);
      setLongDistance(k.longDistance === true);
      setOccurredAt(k.occurredAt);
    },
  );
  useEffect(() => {
    if (postedCount > 0) kept.clear();
  }, [postedCount]);
  // kept.clear for the post's onSuccess, read when it runs: an edit whose
  // replacement is not native hands the box back at once, and the host
  // unmounts this composer before the postedCount effect above can run.
  const keptClearRef = useRef(kept.clear);
  keptClearRef.current = kept.clear;
  // Keep what the person set beside the note (noteComposerFields.ts), on
  // the same terms as the note's own draft: a fresh compose only, and only
  // while the note has words, so a post (which empties the box) drops them.
  useEffect(() => {
    if (localAutosaveOff) return;
    const fields: NoteComposerFields = {};
    if (text.trim() === '') {
      writeNoteComposerFields(fieldsKey, fields, draftAuthor);
      return;
    }
    if (bandPickedRef.current) fields.band = band;
    if (!hoursFollowBandRef.current) fields.hours = hours;
    if (occurredAt !== noteDateDefault()) fields.occurredAt = occurredAt;
    if (afterHours) fields.afterHours = true;
    if (longDistance) fields.longDistance = true;
    writeNoteComposerFields(fieldsKey, fields, draftAuthor);
  }, [fieldsKey, text, band, hours, occurredAt, afterHours, longDistance, localAutosaveOff, draftAuthor]);

  const postMut = useMutation({
    mutationFn: async ({ input, supersedeId }: PostVariables) => {
      if (supersedeId !== null) {
        // 0125 edit path: the server creates the replacement + retires
        // the old note (version link) in one transaction; extraction
        // then runs on the NEW note like any fresh post when the
        // replacement is native; a replacement that is not native is
        // never read, and the box goes back to the host at once.
        const res = await notesApi.supersedeNote(tenantSlug, supersedeId, input);
        return res.note;
      }
      return notesApi.createNote(tenantSlug, clientId, input);
    },
    onMutate: ({ rawText, kind: postedKind, isSupersede: editing, editedNotNative, input, band: postedBand }: PostVariables) => {
      inFlightRef.current = {
        text: rawText,
        hours: input.hours === null || input.hours === undefined ? '' : String(input.hours),
        band: postedBand,
        afterHours: input.after_hours === true,
        longDistance: input.long_distance_travel === true,
        occurredAt: input.occurred_at,
        kind: postedKind,
      };
      // Optimistic clear: the note leaves the box the moment Post is hit.
      setText('');
      if (!editing) writeStoredDraft(key, '', draftAuthor);
      setShowPrefillHint(false);
      setDateRefusal(null);
      // The beat at Post follows the edited note's source, the only value
      // in hand before the server answers: an Edit of a note that is not
      // native is not read, so no beat. Set to idle explicitly, not left
      // as it was, so a refusal from an earlier Save clears as the beat
      // clears it.
      setReading(editing && editedNotNative ? { phase: 'idle' } : { phase: 'reading', kind: postedKind });
      return { rawText };
    },
    onSuccess: (
      note,
      { kind: postedKind, band: postedBand, savedDraftId: consumedDraft, isSupersede: editing, editedNotNative, fromServerDraft },
    ) => {
      setPostedCount((n) => n + 1);
      // The box resets to the band's own default and follows it again
      // (an edit or a saved draft hands the box back to the host instead).
      if (!editing && !fromServerDraft) hoursFollowBandRef.current = true;
      setHours(spiceDefaultHoursForBand(postedBand));
      setAfterHours(false);
      setKind('note');
      setConfirmingZero(false);
      // 0130: the post consumed the server draft — refresh "Your drafts"
      // and hand the box back to the host.
      if (consumedDraft !== null) {
        setSavedDraftId(null);
        setDraftPhase('idle');
        void queryClient.invalidateQueries({
          queryKey: noteDraftsQueryKey(tenantSlug, clientId),
        });
      }
      void queryClient.invalidateQueries({ queryKey: ['kith-notes', tenantSlug, clientId] });
      // The note carries hours (create, or supersede/edit): My time's
      // summary can be stale the moment it lands. It is also a note in a
      // billing month (a new version clears the old one's Ready), so the
      // invoice pages refresh too.
      void invalidateMyHours(queryClient, tenantSlug);
      invalidateInvoiceReads(queryClient, tenantSlug);
      const cached = queryClient.getQueryData<{ proposals: { id: string }[] }>([
        'kith-proposals',
        tenantSlug,
        clientId,
      ]);
      // Whether the read runs follows the answered replacement's source,
      // and the answer wins over the edited note's: a replacement that is
      // not native is never read. The box goes back to the host at once,
      // and the edited note's kept copy is cleared here: the host unmounts
      // this composer in the same batch, so the postedCount effect never
      // runs for it.
      if (editing && !isNativeNote(note)) {
        setReading({ phase: 'idle' });
        keptClearRef.current();
        onSupersedeDone?.();
        return;
      }
      // An edit that read as not native at Post set no beat; a read never
      // runs without its beat.
      if (editing && editedNotNative) {
        setReading({ phase: 'reading', kind: postedKind });
      }
      const baseline = new Set((cached?.proposals ?? []).map((p) => p.id));
      // Supersede: hand control back to the host only after the read
      // settles — the composer keeps the real "Reading your note…" beat
      // and its feed/queue invalidations run to completion first.
      void extractThenReport(note.id, baseline, postedKind).then(() => {
        if (editing) onSupersedeDone?.();
        // 0130: same handoff discipline — the box goes back to the host
        // only after the reading beat settles.
        if (fromServerDraft) onServerDraftDone?.();
      });
    },
    onSettled: () => {
      inFlightRef.current = null;
    },
    onError: (err, { isSupersede: editing }, ctx) => {
      // Restore the draft so nothing the CM wrote is lost.
      if (ctx?.rawText) {
        setText(ctx.rawText);
        if (!editing) writeStoredDraft(key, ctx.rawText, draftAuthor);
      }
      // The same-day refusal, and only that one, is a question rather
      // than an error: with the note back in the box, the footer asks
      // about a second visit (SecondVisitQuestion) in place of the red
      // line. A refusal whose code carries an on-screen sentence keeps the
      // server's message; one a retry cannot fix says so.
      if (serverErrorCodeFrom(err) === 'duplicate_spice_visit') {
        setReading({ phase: 'second_visit_asked' });
        return;
      }
      // A refused date belongs under the date box, which keeps what was
      // typed and takes focus; the footer stays quiet.
      const dateSentence = noteDateRefusal(err);
      if (dateSentence !== null) {
        setDateRefusal(dateSentence);
        setReading({ phase: 'idle' });
        occurredRef.current?.focus();
        return;
      }
      const message = serverMessageFrom(err) ?? refusalText(err, supportContact);
      setReading(message !== null ? { phase: 'error', message } : { phase: 'error' });
    },
  });

  /**
   * 0130 Save as draft (founder ruling A): the server-saved tier —
   * cross-device, listed under "Your drafts" on this client's record.
   * First save CREATES the draft; later saves UPDATE it in place (also
   * when the box opened FROM a draft). Hours may be blank — a draft is
   * allowed to be incomplete; the blank wall applies at post time. The
   * localStorage autosave stays on as the in-flight safety net.
   */
  const saveDraftMut = useMutation({
    mutationFn: async ({ input, savedDraftId: existingDraft }: DraftSaveVariables) => {
      if (existingDraft !== null) {
        return noteDraftsApi.update(tenantSlug, existingDraft, input);
      }
      const res = await noteDraftsApi.create(tenantSlug, clientId, input);
      return res.draft;
    },
    onMutate: () => {
      setDraftPhase('saving');
    },
    onSuccess: (draft, { localAutosaveOff: autosaveOff }) => {
      setSavedDraftId(draft.id);
      setDraftPhase('saved');
      // The box saved server-side; the localStorage copy has served its
      // purpose for this narrative (fresh-compose mode only).
      if (!autosaveOff) writeStoredDraft(key, '', draftAuthor);
      void queryClient.invalidateQueries({
        queryKey: noteDraftsQueryKey(tenantSlug, clientId),
      });
    },
    onError: () => {
      setDraftPhase('error');
    },
  });

  // Hours are required to post a CASE NOTE — but 0 IS VALID (founder feedback
  // 2026-07-06: 0h is a legitimate no-charge / admin moment, so it gets a
  // friendly confirm, not a ban). Only a BLANK field blocks Post; a present
  // value (including 0) enables it. Assessments keep hours optional. The
  // server enforces the same blank-block.
  const hoursNum = Number(hours);
  const hoursPresent = hours.trim() !== '' && Number.isFinite(hoursNum) && hoursNum >= 0;
  const needsHours = kind !== 'assessment' && text.trim().length > 0 && !hoursPresent;
  // WS-8: billable time moves in tenant-config steps (billing.hours_increment,
  // '0.1' fallback = 6 minutes). An off-grid value REFUSES with plain words
  // rather than snapping — a billing quantity is never silently rounded
  // (coerceNoteHours discipline). The server enforces the same rule.
  const hoursIncrement = readHoursIncrement(tenantConfig?.config);
  const hoursOffStep = hoursPresent && !isOnHoursIncrement(hoursNum, hoursIncrement);
  // LP-45: a new note dated inside a closed pay period is posted only by
  // the office (view_all_clinical). For anyone else the line shows under
  // the date at once and Post waits; the server refuses the same post. The
  // cutoff is the server's (pay_closed_through); the screen only compares.
  const payClosedThrough = typeof tenantConfig?.pay_closed_through === 'string' ? tenantConfig.pay_closed_through : null;
  const dateInClosedPeriod =
    !isSupersede &&
    whoami !== null &&
    !canViewAllClinical(whoami) &&
    payClosedThrough !== null &&
    occurredAt !== '' &&
    occurredAt <= payClosedThrough;
  const canPost =
    text.trim().length > 0 &&
    (kind === 'assessment' || hoursPresent) &&
    !hoursOffStep &&
    !dateInClosedPeriod &&
    !postMut.isPending;

  // A stale "Saved as a draft" line clears the moment the box changes.
  useEffect(() => {
    setDraftPhase((p) => (p === 'saved' ? 'idle' : p));
  }, [text, hours, band, occurredAt, afterHours, longDistance, kind]);

  const afterHoursLine = afterHoursLineFor({
    band,
    offered: !noAfterHoursBand,
    afterHours,
    hours,
    day: afterHoursDay(occurredAt, recognizedHolidays(tenantConfig?.config)),
  });

  const canSaveDraft = !isSupersede && text.trim().length > 0 && !saveDraftMut.isPending && !postMut.isPending;

  // 0h posts intercept with a one-time friendly confirm; everything else
  // posts straight through. Blank is already blocked by canPost above.
  function handlePostClick(): void {
    if (kind === 'note' && hoursNum === 0) {
      setConfirmingZero(true);
      return;
    }
    postMut.mutate(postVariables(text, kind, false));
  }

  return (
    <section
      data-testid="note-composer"
      className="bg-paper rounded-card shadow-sh1 border border-sage-800/[8%] p-5 space-y-4"
    >
      {kept.restored && <KeptWorkNotice onDiscard={kept.discard} />}
      <div className="flex items-baseline justify-between gap-3">
        {/* Terracotta heading: SPICE-only colour, never elsewhere — a
            'both' client keeps the case-note heading in the house
            palette even though its rate-band row stays unrestricted. */}
        <h2
          className={`font-display text-xl ${isSpiceEngagement ? 'text-pepper' : 'text-sage-800'}`}
        >
          {isSupersede
            ? 'Edit this note'
            : kind === 'assessment'
              ? 'Post an assessment'
              : isSpiceEngagement
                ? 'Post a session note'
                : 'Post a case note'}
        </h2>
        <span className="text-xs text-ink-soft">
          {isSupersede || serverDraft !== null
            ? null
            : isSpiceEngagement
                ? 'Write one session note. The record and the draft invoice summary come from it.'
                : 'Write one case note. The record and the draft invoice summary come from it.'}
        </span>
      </div>
      {!payLocked && supersedeNote !== null && editorIsNotAuthor(whoami?.user_id, supersedeNote.author_user_id) && (
        <p className="text-xs text-ink" data-testid="composer-supersede-author-hours">
          {NOT_THE_AUTHOR_EDIT_LINE}
        </p>
      )}

      {/* The kind affordance — deliberately QUIET relative to the primary
          case-note action: a small secondary toggle, not a peer button.
          A SPICE face has no toggle at all: a session note is the one
          kind there, and the assessment door belongs to care management. */}
      {!isSpiceEngagement && !onSpiceBand && (
        <div className="flex flex-wrap items-center gap-2" data-testid="composer-kind-toggle">
          {/* One radio group (the house SegmentedToggle). The kind changes
              the Post button's words; After Hours never does. */}
          <SegmentedToggle
            options={KIND_OPTIONS}
            value={kind}
            onChange={setKind}
            name="What are you posting?"
            testIdPrefix="composer-kind"
            wrap
          />
          {kind === 'assessment' && (
            <span data-testid="composer-assessment-hint" className="text-xs text-ink-soft">
              Paste the assessment. It fills in the client's record for you to confirm.
            </span>
          )}
        </div>
      )}

      {showPrefillHint && (
        <div
          data-testid="composer-prefill-hint"
          className="flex items-center justify-between gap-3 rounded-base border border-sage-800/[8%] bg-sand px-3 py-2"
        >
          <p className="text-xs text-ink">
            {prefill?.source === 'spice_decline'
              ? 'Pre-filled from the decline form. Review before posting.'
              : 'Pre-filled from your email draft. Review before posting.'}
          </p>
          <Button
            size="sm"
            variant="ghost"
            data-testid="composer-prefill-dismiss"
            onClick={() => setShowPrefillHint(false)}
          >
            Dismiss
          </Button>
        </div>
      )}

      <div>
        <label htmlFor="note-composer-text" className="sr-only">
          Note
        </label>
        <TextArea
          id="note-composer-text"
          data-testid="composer-textarea"
          className={`${FLD_TEXTAREA} min-h-[120px]`}
          placeholder="What happened on this visit or call?"
          value={text}
          maxLength={charLimit}
          // A locked note's sentence is read with the box, since its four
          // controls are out of the tab order.
          aria-describedby={payLocked ? 'note-composer-counter composer-pay-locked' : 'note-composer-counter'}
          onChange={(e) => {
            const next = e.target.value;
            setText(next.length > charLimit ? next.slice(0, charLimit) : next);
          }}
        />
        <p
          id="note-composer-counter"
          data-testid="composer-counter"
          className="mt-1.5 text-xs text-ink-soft"
        >
          {text.length > COUNTER_VISIBLE_FROM && (
            <span data-testid="composer-char-count">
              {text.length.toLocaleString()} characters
            </span>
          )}
        </p>
      </div>

      {payLocked && (
        <p id="composer-pay-locked" className="text-xs text-ink" data-testid="composer-pay-locked">
          {PAY_PERIOD_CLOSED_EDIT_LINE}
        </p>
      )}
      <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
        {offersOnlySpiceBands ? (
          <div>
            <span id="composer-spice-band-label" className={FLD_LABEL}>
              Session
            </span>
            <SpiceBandToggle
              value={
                band === SPICE_FRIENDLY_VISITOR_BAND || (spiceToggleExtra !== null && band === spiceToggleExtra.value)
                  ? band
                  : SPICE_BAND
              }
              onChange={pickBand}
              extra={spiceToggleExtra}
              testIdPrefix="composer-spice-band"
              labelledBy="composer-spice-band-label"
              disabled={payLocked}
            />
          </div>
        ) : (
          <div className="w-full min-w-0 sm:w-auto">
            <Field label="Rate">
              <div
                role="radiogroup"
                aria-label="Rate"
                data-testid="composer-rate-bands"
                // LP-28: below sm the picker is a two-column grid of equal
                // pills at full width (an odd last pill takes the whole
                // row), so the choices never wrap into a ragged second
                // row; from sm up it is the content-sized row that folds
                // whole pills onto a second line when it must (S3.5).
                // Every option stays visible and nothing clips.
                className={RATE_BANDS_GROUP_CLS}
              >
                {rateBands.map((rb, i) => (
                  <Button
                    key={rb.value}
                    ref={(el) => {
                      bandRadios.current[i] = el;
                    }}
                    role="radio"
                    aria-checked={band === rb.value}
                    // A roving tab stop: the checked pill, or the first when
                    // none is; arrows move the check (radioGroupKeys).
                    tabIndex={(band === rb.value || (!rateBands.some((x) => x.value === band) && i === 0)) ? 0 : -1}
                    onKeyDown={onBandKey}
                    disabled={payLocked}
                    size="sm"
                    // The SPICE accent role: the selected band pill takes
                    // pepper on a SPICE face, forest everywhere else.
                    variant={
                      band === rb.value ? (isSpiceEngagement ? 'pepper' : 'primary') : 'ghost'
                    }
                    className={RATE_BAND_PILL_CLS}
                    data-testid={`composer-band-${rb.value}`}
                    onClick={() => pickBand(rb.value)}
                  >
                    {rb.label}
                  </Button>
                ))}
              </div>
            </Field>
          </div>
        )}

        {/* Hours and Occurred on are grouped in their own row so the
            number of rate-band pills above never pushes one away from the
            other — the band picker wraps freely on its own line; this
            pair wraps (if it must) as its own unit. */}
        <div data-testid="composer-hours-occurred-group" className="flex min-w-0 max-w-full flex-wrap items-end gap-x-5 gap-y-3">
          <Field label="Hours" htmlFor="note-composer-hours">
            <input
              id="note-composer-hours"
              data-testid="composer-hours"
              className={`${FLD} w-24 disabled:cursor-not-allowed disabled:opacity-50`}
              type="number"
              inputMode="decimal"
              min={0}
              step={Number(hoursIncrement)}
              value={hours}
              disabled={payLocked}
              onChange={(e) => {
                hoursFollowBandRef.current = false;
                setHours(e.target.value);
                // Editing hours dismisses a stale 0h confirm.
                setConfirmingZero(false);
              }}
            />
          </Field>

          {/* min-w-0 wrapper: a date input's UA intrinsic width otherwise
              props this flex item past narrow zoomed viewports (195px at
              200% iOS page zoom). */}
          <div className="min-w-0 max-w-full">
          <Field
            label="Occurred on"
            htmlFor="note-composer-occurred"
            error={dateRefusal ?? (dateInClosedPeriod ? PAY_PERIOD_CLOSED_POST_LINE : undefined)}
          >
            <DateInput
              id="note-composer-occurred"
              ref={occurredRef}
              data-testid="composer-occurred"
              size="field" className="w-full max-w-[10rem] min-w-0"
              value={occurredAt}
              max={practiceTodayIso()}
              disabled={payLocked}
              aria-invalid={dateRefusal !== null || dateInClosedPeriod ? true : undefined}
              aria-describedby={dateRefusal !== null || dateInClosedPeriod ? 'note-composer-occurred-error' : undefined}
              onChange={(e) => {
                setOccurredAt(e.target.value);
                setDateRefusal(null);
              }}
            />
          </Field>
          </div>

          {/* Regular | After Hours, with the date it describes. Absent on
              the admin band (isAdminBand above). Below sm it takes its own
              line at the full width of the fields' column, the same left
              edge as Hours and Occurred on. From sm up it is centred on the
              fields' middle: the row aligns its items at the bottom, and
              this wrapper is at least the fields' height (FLD's 46px) and
              centres the control inside it. */}
          {!noAfterHoursBand && (
            <div className="grow basis-full sm:grow-0 sm:basis-auto sm:flex sm:min-h-[46px] sm:items-center">
              <AfterHoursToggle
                value={afterHours}
                onChange={setAfterHours}
                testIdPrefix="composer-after-hours"
                disabled={payLocked}
              />
            </div>
          )}

          {/* The fact of the day, directly under the toggle. Words to act
              on, so it wraps and is never truncated. */}
          {afterHoursLine !== null && (
            <p
              data-testid="composer-after-hours-nudge"
              role="status"
              className="basis-full text-xs text-ink-soft"
            >
              {afterHoursLine}
            </p>
          )}
        </div>

        {/* A long-distance SPICE visit (LP-46): on a SPICE-family band only,
            under Hours. It pays the author one SPICE Travel line in the
            payroll file and is never on the client's bill. The helper
            says the tenant's rule for which visits count
            (payroll.long_distance_rule) and never an amount: Olive shows
            no money to a care manager or a therapist; the pay itself
            lives in the payroll system. */}
        {onSpiceBand && (
          <div data-testid="composer-long-distance-row">
            <CheckboxRow
              id="note-composer-long-distance"
              testId="composer-long-distance"
              label="Long-distance visit"
              checked={longDistance}
              onChange={setLongDistance}
              disabled={payLocked}
            />
            <p className="text-xs text-ink-soft">{longDistanceHelper(readLongDistanceRule(tenantConfig?.config))}</p>
          </div>
        )}

      </div>

      {needsHours && (
        <p data-testid="composer-hours-nudge" className="text-xs text-ink-soft">
          Add hours to post this case note.
        </p>
      )}

      {hoursOffStep && (
        <p data-testid="composer-hours-step" className="text-xs text-pepper">
          {hoursIncrementMessage(hoursIncrement, hoursNum)}
        </p>
      )}

      {/* WS-9 P0 (founder): the reading beat is front and center — its own
          full-width band in the drafters' treatment with a live elapsed
          counter (ReadingIndicator), never a footnote in the status line.
          The band stays mounted for the WHOLE awaited extract, so the
          counter keeps ticking through the WS-9 auto-retry pauses. */}
      {reading.phase === 'reading' && (
        <ReadingIndicator
          kind={reading.kind}
          progress={reading.progress ?? null}
          testId="composer-reading"
        />
      )}

      {/* B1 THE GASP (Design Pass 2): extraction done → a staged, warm
          reveal of what one note became, in the gold awaiting-confirm
          register (the proposals it announces ARE awaiting confirms).
          One tasteful entrance (a settle, not confetti); reduced motion
          renders it still. The invoice sentence appears only when the
          drafted line is a verified fact from the refreshed feed cache. */}
      {reading.phase === 'done' && (
        <div
          role="status"
          data-testid="composer-gasp"
          className="animate-rise-in motion-reduce:animate-none rounded-base border border-warm-400/40 bg-gold-soft/50 px-4 py-3"
        >
          <p className="font-display text-[15.5px] leading-snug text-sage-800">
            From one note: {gaspPhrase(reading.categories)}.
          </p>
          {reading.invoiceLineDrafted === true && (
            <p data-testid="composer-gasp-invoice" className="mt-1 text-[12.5px] text-ink-soft">
              Your invoice summary is drafted.
            </p>
          )}
          <p className="mt-1 text-[12px] text-ink-soft">
            Everything waits for your yes under To confirm.
          </p>
        </div>
      )}

      <div className="pt-1 border-t border-sage-800/[8%]">
        {reading.phase === 'second_visit_asked' ? (
          // The same-day question takes the footer's place, one door
          // through, as the zero-hours confirm takes the action cluster's.
          // Confirming re-enters the post with the note text as it stands
          // in the box, so the optimistic clear, the reading beat and the
          // read run exactly as on a first post; keeping the first note
          // hands the footer back with the note still in the box.
          <SecondVisitQuestion
            confirmLabel="Post a second visit"
            onConfirm={() => postMut.mutate(postVariables(text, kind, true))}
            onKeep={() => setReading({ phase: 'idle' })}
            testIds={{
              question: 'composer-second-visit-warning',
              confirm: 'composer-post-second-visit',
              keep: 'composer-keep-first-note',
            }}
          />
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            {/* The footer wraps: on a narrow line the actions sit under the
                status, and the buttons wrap among themselves. */}
            <p aria-live="polite" data-testid="composer-status" className="text-xs min-h-4">
              {reading.phase === 'none' && (
                <span className="text-ink-soft">
                  Posted. Nothing new to confirm from this note.
                </span>
              )}
              {reading.phase === 'read_failed' && (
                <span role="alert" className="text-pepper">
                  {errorText('note_read', reading.key, supportContact)}
                </span>
              )}
              {reading.phase === 'error' && (
                <span role="alert" className="text-pepper">
                  {reading.message ?? errorText('note_post', 'failed', supportContact)}
                </span>
              )}
              {reading.phase === 'idle' && draftPhase === 'saved' && (
                <span className="text-ink-soft" data-testid="composer-draft-saved">
                  Saved as a draft. Find it under Your drafts on this record.
                </span>
              )}
              {reading.phase === 'idle' && draftPhase === 'error' && (
                <span role="alert" className="text-pepper" data-testid="composer-draft-error">
                  {errorText('note_draft_save', 'failed', supportContact)}
                </span>
              )}
            </p>
            {confirmingZero && kind === 'note' ? (
              <div
                data-testid="composer-zero-confirm"
                className="flex min-w-0 flex-wrap items-center gap-2 text-right"
              >
                <span className="text-xs text-ink">
                  This case note is 0 hours. Is that intentional?
                </span>
                <Button
                  size="sm"
                  data-testid="composer-zero-confirm-yes"
                  loading={postMut.isPending}
                  onClick={() => {
                    setConfirmingZero(false);
                    postMut.mutate(postVariables(text, kind, false));
                  }}
                >
                  Yes, I meant 0 hours
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid="composer-zero-confirm-back"
                  onClick={() => setConfirmingZero(false)}
                >
                  Go back
                </Button>
              </div>
            ) : (
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                {isSupersede && (
                  <Button
                    variant="ghost"
                    data-testid="composer-supersede-cancel"
                    onClick={() => onSupersedeCancel?.()}
                  >
                    Cancel
                  </Button>
                )}
                {serverDraft !== null && (
                  <Button
                    variant="ghost"
                    data-testid="composer-draft-close"
                    onClick={() => onServerDraftDone?.()}
                  >
                    Close
                  </Button>
                )}
                {!isSupersede && (
                  <Button
                    variant="secondary"
                    data-testid="composer-save-draft"
                    disabled={!canSaveDraft}
                    loading={saveDraftMut.isPending}
                    onClick={() =>
                      saveDraftMut.mutate({
                        input: {
                          kind,
                          body: text,
                          occurred_on: occurredAt,
                          rate_band: band,
                          hours: hours.trim() === '' ? null : Number(hours),
                          after_hours: afterHours,
                          long_distance_travel: longDistance,
                        },
                        savedDraftId,
                        localAutosaveOff,
                      })
                    }
                  >
                    Save as draft
                  </Button>
                )}
                <Button
                  data-testid="composer-post"
                  // The SPICE accent role: the composer's one primary action
                  // takes pepper on a SPICE face, forest everywhere else.
                  variant={isSpiceEngagement ? 'pepper' : 'primary'}
                  disabled={!canPost}
                  loading={postMut.isPending}
                  onClick={handlePostClick}
                >
                  {isSupersede
                    ? 'Save as a new version'
                    : kind === 'assessment'
                      ? 'Post assessment'
                      : isSpiceEngagement
                        ? 'Post session note'
                        : 'Post case note'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
